import { getPlayablePages, getElementsForViewport } from "../components/escape-room/erViewport";
import { isRegistrationPage } from "./escapeRoomConstants";
import { isEscapeRoomVideoUrl } from "../components/escape-room/escapeRoomBackgroundMedia";

const GRAPH_VIEWPORTS = ["desktop", "tablet", "mobile"];

const CHOICE_TYPES = new Set(["multipleChoice", "trueFalse", "imageChoice", "optionsWithImages"]);
const NAV_ACTIONS = new Set(["nextPage", "goToPage"]);

function normScId(v) {
	if (v == null || v === "") return "";
	return String(v).trim();
}

function resolveQuizId(rawQuizId) {
	if (!rawQuizId) return null;
	if (typeof rawQuizId === "string") return rawQuizId;
	if (typeof rawQuizId === "object") {
		if (rawQuizId._id) return String(rawQuizId._id);
		if (rawQuizId.id) return String(rawQuizId.id);
	}
	return null;
}

function findPageIndex(pages, tid) {
	const t = String(tid ?? "");
	if (!t) return -1;
	const idx = pages.findIndex((p) => String(p.pageId || p._id) === t);
	if (idx >= 0) return idx;
	return pages.findIndex((p) => String(p._id) === t || String(p.pageId) === t);
}

/** Elementos de la página (incl. modales) unificando desktop/tablet/mobile. */
function collectAllPageElements(page) {
	const byId = new Map();
	const extras = [];

	const walk = (elements) => {
		if (!Array.isArray(elements)) return;
		for (const e of elements) {
			const id = e?.elementId != null ? String(e.elementId) : "";
			if (id) {
				if (!byId.has(id)) byId.set(id, e);
			} else {
				extras.push(e);
			}
			if (e?.type === "button" && e.content?.modalDesign?.elements?.length) {
				walk(e.content.modalDesign.elements);
			}
			if (e?.type === "hint") {
				const c = e.content || {};
				for (const key of ["hintModalDesign", "confirmModalDesign"]) {
					if (c[key]?.elements?.length) walk(c[key].elements);
				}
			}
		}
	};

	for (const vp of GRAPH_VIEWPORTS) {
		walk(getElementsForViewport(page, vp) || []);
	}
	return [...byId.values(), ...extras];
}


function walkOutgoingPageIndices(elements, pages, pageIdx, findIdx, out) {
	if (!elements?.length) return;
	for (const e of elements) {
		if (e.type === "button") {
			const a = e.content?.action;
			if (a === "nextPage") {
				out.add(Math.min(pages.length - 1, pageIdx + 1));
			} else if (a === "goToPage" && e.content?.targetPageId) {
				const ix = findIdx(e.content.targetPageId);
				if (ix >= 0) out.add(ix);
			} else if (a === "openModal" && e.content?.modalDesign?.elements?.length) {
				walkOutgoingPageIndices(e.content.modalDesign.elements, pages, pageIdx, findIdx, out);
			}
		} else if (e.type === "hint") {
			const c = e.content || {};
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				const des = c[key];
				if (des?.elements?.length) walkOutgoingPageIndices(des.elements, pages, pageIdx, findIdx, out);
			}
		}
	}
}

function collectOutgoingPageIndicesFromPage(pages, pageIdx) {
	const findIdx = (tid) => findPageIndex(pages, tid);
	const out = new Set();
	for (const vp of GRAPH_VIEWPORTS) {
		const els = getElementsForViewport(pages[pageIdx], vp) || [];
		walkOutgoingPageIndices(els, pages, pageIdx, findIdx, out);
	}
	return out;
}

function ufFind(parent, x) {
	const k = normScId(x);
	if (!k) return "";
	if (!parent.has(k)) parent.set(k, k);
	if (parent.get(k) === k) return k;
	const r = ufFind(parent, parent.get(k));
	parent.set(k, r);
	return r;
}

function ufUnion(parent, a, b) {
	const ra = ufFind(parent, normScId(a));
	const rb = ufFind(parent, normScId(b));
	if (ra && rb && ra !== rb) parent.set(ra, rb);
}

function computeExclusiveBranchGroups(pages) {
	const parent = new Map();
	const getOut = (pi) => collectOutgoingPageIndicesFromPage(pages, pi);

	// Solo bifurcaciones reales: misma pantalla con salidas a varios escenarios distintos.
	for (let i = 0; i < pages.length; i++) {
		const cur = normScId(pages[i]?.scenarioId);
		const scenFromPage = new Set();
		for (const t of getOut(i)) {
			const sc = normScId(pages[t]?.scenarioId);
			if (sc && sc !== cur) scenFromPage.add(sc);
		}
		if (scenFromPage.size >= 2) {
			const ids = [...scenFromPage];
			for (let j = 1; j < ids.length; j++) ufUnion(parent, ids[0], ids[j]);
		}
	}

	const allScenarioIds = new Set();
	pages.forEach((pg) => {
		const x = normScId(pg?.scenarioId);
		if (x) allScenarioIds.add(x);
	});
	const byRoot = new Map();
	allScenarioIds.forEach((id) => {
		const r = ufFind(parent, id);
		if (!r) return;
		if (!byRoot.has(r)) byRoot.set(r, new Set());
		byRoot.get(r).add(id);
	});
	return Array.from(byRoot.values()).filter((g) => g.size >= 2);
}

function applySuppressBranchAlternates(landedScenarioId, branchGroups, suppressed) {
	const landed = normScId(landedScenarioId);
	if (!landed) return suppressed;
	const g = branchGroups.find((gr) => gr.has(landed));
	if (!g || g.size < 2) return suppressed;
	const next = new Set(suppressed);
	g.forEach((id) => {
		const x = normScId(id);
		if (x && x !== landed) next.add(x);
	});
	return next;
}

function isScenarioSuppressed(page, suppressed) {
	const sc = normScId(page?.scenarioId);
	return sc !== "" && suppressed.has(sc);
}

function collectNavButtonsFromElements(elements) {
	const out = [];
	if (!elements?.length) return out;
	for (const e of elements) {
		if (e.type === "button") {
			const action = e.content?.action;
			if (NAV_ACTIONS.has(action)) {
				if (action === "goToPage" && !e.content?.targetPageId) continue;
				out.push(e);
			} else if (e.content?.action === "openModal" && e.content?.modalDesign?.elements?.length) {
				out.push(...collectNavButtonsFromElements(e.content.modalDesign.elements));
			}
		} else if (e.type === "hint") {
			const c = e.content || {};
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				const des = c[key];
				if (des?.elements?.length) out.push(...collectNavButtonsFromElements(des.elements));
			}
		}
	}
	return out;
}

function getNavButtonsForPage(pages, pageIdx) {
	const byId = new Map();
	const els = collectAllPageElements(pages[pageIdx]);
	for (const btn of collectNavButtonsFromElements(els)) {
		const id = btn.elementId != null ? String(btn.elementId) : null;
		if (id && !byId.has(id)) byId.set(id, btn);
	}
	const page = pages[pageIdx];
	const bgUrl = page?.backgroundImageUrl ? String(page.backgroundImageUrl) : "";
	if (page?.advanceOnBackgroundVideoEnd && bgUrl && isEscapeRoomVideoUrl(bgUrl)) {
		byId.set(`__auto_next_${pageIdx}`, { elementId: `__auto_next_${pageIdx}`, content: { action: "nextPage", onlyOnce: false } });
	}
	const buttons = [...byId.values()];
	// Fallback lineal: páginas sin botones explícitos (orden del array).
	if (buttons.length === 0 && pageIdx < pages.length - 1) {
		buttons.push({
			elementId: `__linear_next_${pageIdx}`,
			content: { action: "nextPage", onlyOnce: false },
		});
	}
	return buttons;
}

function resolveNavTarget(pages, pageIdx, button) {
	const action = button?.content?.action;
	if (action === "nextPage") return Math.min(pages.length - 1, pageIdx + 1);
	if (action === "goToPage" && button.content?.targetPageId) {
		return findPageIndex(pages, button.content.targetPageId);
	}
	return -1;
}

function serializeUsedButtons(usedByPage) {
	return Object.keys(usedByPage)
		.sort((a, b) => Number(a) - Number(b))
		.map((k) => `${k}:${[...usedByPage[k]].sort().join(",")}`)
		.join("|");
}

function serializeVisited(visited) {
	return [...visited].sort((a, b) => a - b).join(",");
}

function serializeSuppressed(suppressed) {
	return [...suppressed].sort().join(",");
}

/** Puntos máximos de un quiz si se responde de forma óptima (sin pistas). */
export function computeMaxQuizScore(quiz) {
	if (!quiz) return 0;
	if (String(quiz.gradingMode || "").toLowerCase() === "survey") return 0;

	const pointsCorrect = quiz.pointsCorrect ?? 10;
	const firstQ = quiz.questions?.[0];
	if (!firstQ) return 0;

	const attemptMode = quiz.attemptMode || (quiz.allowMultipleAttempts ? "unlimited" : "single");
	const maxAttempts = Math.max(1, quiz.maxAttempts ?? 3);
	const qType = String(firstQ.type || "");

	if (CHOICE_TYPES.has(qType)) {
		const opts = firstQ.options || [];
		const totalCorrect = opts.filter((o) => o?.correct).length;
		if (attemptMode === "single") {
			return totalCorrect > 0 ? pointsCorrect : 0;
		}
		if (attemptMode === "limited") {
			return Math.min(totalCorrect, maxAttempts) * pointsCorrect;
		}
		return totalCorrect * pointsCorrect;
	}

	if (qType === "match") {
		const totalCorrect = (firstQ.matchPairs || []).length;
		return totalCorrect * pointsCorrect;
	}

	if (qType === "fillBlanks") {
		const totalCorrect = (firstQ.segments || []).filter((s) => s?.type === "blank").length;
		return totalCorrect * pointsCorrect;
	}

	if (qType === "textAnswer") {
		return pointsCorrect;
	}

	return 0;
}

function quizMaxOnPage(page, quizzesById) {
	const seen = new Set();
	let sum = 0;
	const els = collectAllPageElements(page);
	for (const el of els) {
		if (el?.type !== "quiz") continue;
		const eid = String(el.elementId || "");
		if (!eid || seen.has(eid)) continue;
		seen.add(eid);
		const qid = resolveQuizId(el.content?.quizId);
		if (!qid) continue;
		const quiz = quizzesById[qid];
		if (quiz) {
			sum += computeMaxQuizScore(quiz);
		} else {
			sum += 10;
		}
	}
	return sum;
}

function scoreForPageIndex(pages, pageIdx, quizzesById) {
	const page = pages[pageIdx];
	if (!page || isRegistrationPage(page)) return 0;
	return landingPointsOnPage(page) + quizMaxOnPage(page, quizzesById) + addPointsButtonsOnPage(page);
}

/** Recorrido lineal por orden de páginas (fallback si el grafo no detecta navegación). */
export function computeLinearOrderedMaxScore(pagesInput, quizzesById = {}) {
	const pages = getPlayablePages(Array.isArray(pagesInput) ? pagesInput : []);
	let total = 0;
	let pastRegistration = false;
	for (let i = 0; i < pages.length; i++) {
		if (!pastRegistration) {
			if (isRegistrationPage(pages[i])) continue;
			pastRegistration = true;
		}
		total += scoreForPageIndex(pages, i, quizzesById);
	}
	return total;
}

function landingPointsOnPage(page) {
	const pts = Number(page?.pointsOnLand ?? 0);
	return Number.isFinite(pts) ? pts : 0;
}

/** Suma de puntos positivos de botones «addPoints» en la página (una vez por botón). */
function addPointsButtonsOnPage(page) {
	let sum = 0;
	for (const el of collectAllPageElements(page)) {
		if (el?.type !== "button") continue;
		if (String(el?.content?.action || "") !== "addPoints") continue;
		const d = Number(el?.content?.pointsDelta);
		if (Number.isFinite(d) && d > 0) sum += d;
	}
	return sum;
}

function findStartPageIndex(pages) {
	const firstNonReg = pages.findIndex((p) => !isRegistrationPage(p));
	return firstNonReg >= 0 ? firstNonReg : 0;
}

function pagesUseExplicitBranching(pages) {
	for (let i = 0; i < pages.length; i += 1) {
		for (const btn of getNavButtonsForPage(pages, i)) {
			if (btn.content?.action === "goToPage") return true;
		}
	}
	return false;
}

/**
 * Puntuación máxima teórica del escape room:
 * - Puntos por aterrizar en cada pantalla alcanzable (una vez por pantalla).
 * - Quizzes respondidos de forma óptima en pantallas alcanzables.
 * - Ramas exclusivas, botones onlyOnce y escenarios suprimidos según el grafo de juego.
 */
export function computeEscapeRoomMaxScore(pagesInput, quizzesById = {}) {
	const pages = getPlayablePages(Array.isArray(pagesInput) ? pagesInput : []);
	if (!pages.length) return 0;

	const branchGroups = computeExclusiveBranchGroups(pages);
	const navButtonsByPage = pages.map((_, pageIdx) => getNavButtonsForPage(pages, pageIdx));
	const memo = new Map();
	const visiting = new Set();

	function dfs(pageIdx, usedByPage, suppressed, visited) {
		const key = `${pageIdx}#${serializeUsedButtons(usedByPage)}#${serializeSuppressed(suppressed)}#${serializeVisited(visited)}`;
		if (memo.has(key)) return memo.get(key);
		if (visiting.has(key)) return 0;

		visiting.add(key);

		let gain = 0;
		const visitedNext = new Set(visited);
		if (!visited.has(pageIdx)) {
			visitedNext.add(pageIdx);
			gain += scoreForPageIndex(pages, pageIdx, quizzesById);
		}

		const pageUsed = usedByPage[pageIdx] || new Set();
		const navButtons = (navButtonsByPage[pageIdx] || []).filter((btn) => {
			if (btn.content?.onlyOnce && pageUsed.has(btn.elementId)) return false;
			return true;
		});

		let bestChild = 0;
		for (const btn of navButtons) {
			const targetIdx = resolveNavTarget(pages, pageIdx, btn);
			if (targetIdx < 0 || targetIdx >= pages.length || targetIdx === pageIdx) continue;
			if (isScenarioSuppressed(pages[targetIdx], suppressed)) continue;

			const nextUsed = { ...usedByPage };
			if (btn.content?.onlyOnce && btn.elementId != null) {
				const set = new Set(pageUsed);
				set.add(btn.elementId);
				nextUsed[pageIdx] = set;
			}

			const landedSc = pages[targetIdx]?.scenarioId;
			const nextSuppressed = applySuppressBranchAlternates(landedSc, branchGroups, suppressed);

			const child = dfs(targetIdx, nextUsed, nextSuppressed, visitedNext);
			bestChild = Math.max(bestChild, child);
		}

		const total = gain + bestChild;
		visiting.delete(key);
		memo.set(key, total);
		return total;
	}

	const startIdx = findStartPageIndex(pages);
	const dfsScore = dfs(startIdx, {}, new Set(), new Set());
	const linearScore = computeLinearOrderedMaxScore(pages, quizzesById);
	// Con bifurcaciones reales el DFS es la fuente de verdad; el lineal sumaría ramas excluyentes.
	const best = pagesUseExplicitBranching(pages) ? dfsScore : Math.max(dfsScore, linearScore);
	return best > 0 ? best : 0;
}

function pagesHavePlayableContent(pages) {
	if (!Array.isArray(pages) || pages.length === 0) return false;
	return pages.some(
		(p) =>
			(Array.isArray(p?.elements) && p.elements.length > 0)
			|| Number(p?.pointsOnLand) !== 0
			|| isRegistrationPage(p)
	);
}

export { pagesHavePlayableContent };

/** Recoge ids de quiz referenciados en las páginas del escape room. */
export function collectQuizIdsFromPages(pagesInput) {
	const pages = getPlayablePages(Array.isArray(pagesInput) ? pagesInput : []);
	const ids = new Set();
	for (const page of pages) {
		for (const el of collectAllPageElements(page)) {
			if (el?.type !== "quiz") continue;
			const qid = resolveQuizId(el.content?.quizId);
			if (qid) ids.add(qid);
		}
	}
	return [...ids];
}
